import React, { useState, useRef, useEffect, useCallback, useId } from 'react';
import ReactCountryFlag from 'react-country-flag';
import * as Popover from '@radix-ui/react-popover';
import { useTranslation } from 'react-i18next';
import { cn } from '../utils/cn';
import { Spinner } from './ui/Spinner';

interface CurrencyDropdownProps {
  options: any[];
  value: any;
  onOptionChange?: (option: any) => void;
  label?: string;
  readOnly?: boolean;
  className?: string;
}

function getCountryCode(option: any): string {
  return (
    option?.foreignCurrency?.country?.iso?.toLowerCase() ||
    option?.foreignCurrency?.country?.iso3?.toLowerCase() ||
    ''
  );
}

function getCurrencyCode(option: any): string {
  return option?.foreignCurrency?.currencyCode ?? '';
}

const CurrencyDropdown: React.FC<CurrencyDropdownProps> = ({
  options,
  value,
  onOptionChange,
  label,
  readOnly = false,
  className,
}) => {
  const { t } = useTranslation(['common', 'translation']);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [activeIndex, setActiveIndex] = useState(-1);
  const listRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const listboxId = useId();

  const selected = value !== 0 && value !== null ? value : options[0];

  const filtered = search
    ? options.filter((opt) =>
        getCurrencyCode(opt).toLowerCase().includes(search.toLowerCase())
      )
    : options;

  // Reset search and active index when popover opens/closes
  useEffect(() => {
    if (open) {
      setSearch('');
      setActiveIndex(-1);
      // Focus search input when popover opens
      requestAnimationFrame(() => searchInputRef.current?.focus());
    }
  }, [open]);

  const selectOption = useCallback(
    (option: any) => {
      onOptionChange?.(option);
      setOpen(false);
    },
    [onOptionChange]
  );

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((prev) => Math.min(prev + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((prev) => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter' && activeIndex >= 0 && activeIndex < filtered.length) {
      e.preventDefault();
      selectOption(filtered[activeIndex]);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  // Scroll active option into view
  useEffect(() => {
    if (activeIndex >= 0 && listRef.current) {
      const activeEl = listRef.current.querySelector(`[data-index="${activeIndex}"]`);
      activeEl?.scrollIntoView({ block: 'nearest' });
    }
  }, [activeIndex]);

  if (options.length === 0) {
    return (
      <div className={cn('flex items-center justify-center w-[200px]', className)} role="status">
        <Spinner size="md" className="text-primary" aria-label={t('common:Loading currencies')} />
      </div>
    );
  }

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild disabled={readOnly}>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          aria-haspopup="listbox"
          aria-controls={listboxId}
          aria-label={label ?? t('common:Select Currency')}
          disabled={readOnly}
          className={cn(
            'flex items-center gap-2 rounded-md border border-input bg-background px-3 py-1.5 text-sm',
            'hover:bg-accent transition-colors w-[200px]',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary',
            'disabled:cursor-not-allowed disabled:opacity-50',
            className
          )}
        >
          <ReactCountryFlag
            countryCode={getCountryCode(selected)}
            svg
            style={{ width: '1.5em', height: '1.5em' }}
          />
          <span className="font-medium truncate">{getCurrencyCode(selected)}</span>
          <svg className="ms-auto h-4 w-4 opacity-50 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="m6 9 6 6 6-6" />
          </svg>
        </button>
      </Popover.Trigger>

      <Popover.Portal>
        <Popover.Content
          className="z-50 w-[220px] rounded-md border border-border bg-card shadow-md animate-in fade-in-0 zoom-in-95"
          sideOffset={4}
          align="start"
        >
          <div className="p-2 border-b border-border">
            <input
              ref={searchInputRef}
              type="text"
              role="combobox"
              aria-expanded={true}
              aria-controls={listboxId}
              aria-activedescendant={activeIndex >= 0 ? `currency-option-${activeIndex}` : undefined}
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setActiveIndex(-1);
              }}
              onKeyDown={handleKeyDown}
              placeholder={t('common:Search') + '...'}
              aria-label={t('common:Search currencies')}
              className="w-full rounded-sm border border-input bg-background px-2 py-1 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary/20 focus-visible:border-primary"
            />
          </div>
          <div
            ref={listRef}
            role="listbox"
            id={listboxId}
            aria-label={label ?? t('common:Select Currency')}
            className="max-h-48 overflow-y-auto p-1"
          >
            {filtered.length === 0 ? (
              <div className="px-2 py-3 text-sm text-muted-foreground text-center">
                {t('common:No results')}
              </div>
            ) : (
              filtered.map((option, index) => {
                const isSelected = getCurrencyCode(option) === getCurrencyCode(selected);
                return (
                  <div
                    key={getCurrencyCode(option) || index}
                    id={`currency-option-${index}`}
                    role="option"
                    aria-selected={isSelected}
                    data-index={index}
                    onClick={() => selectOption(option)}
                    className={cn(
                      'flex items-center gap-2 rounded-sm px-2 py-1.5 text-sm cursor-pointer transition-colors',
                      index === activeIndex && 'bg-muted',
                      isSelected && 'font-semibold',
                      index !== activeIndex && 'hover:bg-muted'
                    )}
                  >
                    <ReactCountryFlag
                      countryCode={getCountryCode(option)}
                      svg
                      style={{ width: '1.5em', height: '1.5em' }}
                    />
                    <span className="font-semibold text-sm">{getCurrencyCode(option)}</span>
                  </div>
                );
              })
            )}
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};

export default CurrencyDropdown;
